import React from 'react';

import { Button, Card } from '@/components/ui';
import type { EntertainmentBalance } from '@/lib/entertainment/ledger-types';
import type { User } from '@/lib/types';
import { formatCurrency } from '@/lib/utils';

type Props = {
  currentUserId: string;
  users: User[];
  balances: EntertainmentBalance[];
  canChoosePayer?: boolean;
  onCreatePayment: (body: { payer_id: string; payee_id: string; amount_cents: number }) => void | Promise<void>;
};

function suggestedPayeeId(balances: EntertainmentBalance[], payerId: string): string {
  return [...balances]
    .filter((balance) => balance.user_id !== payerId && balance.official_balance_cents > 0)
    .sort((left, right) => right.official_balance_cents - left.official_balance_cents)[0]?.user_id ?? '';
}

export function PaymentForm({ currentUserId, users, balances, canChoosePayer = false, onCreatePayment }: Props) {
  const [payerId, setPayerId] = React.useState(currentUserId);
  const effectivePayerId = canChoosePayer ? payerId : currentUserId;
  const [payeeId, setPayeeId] = React.useState(() => suggestedPayeeId(balances, effectivePayerId));
  const [amount, setAmount] = React.useState('');

  React.useEffect(() => {
    if (!canChoosePayer) {
      setPayerId(currentUserId);
      return;
    }
    setPayerId((previous) => previous || currentUserId);
  }, [canChoosePayer, currentUserId]);

  React.useEffect(() => {
    setPayeeId((previous) => {
      const previousStillValid = previous
        && previous !== effectivePayerId
        && users.some((user) => user.id === previous);
      return previousStillValid ? previous : suggestedPayeeId(balances, effectivePayerId);
    });
  }, [balances, effectivePayerId, users]);

  const amountCents = Math.round(Number(amount) * 100);
  const payeeBalance = balances.find((balance) => balance.user_id === payeeId)?.official_balance_cents ?? 0;
  const payerOwes = Math.abs(Math.min(0, balances.find((balance) => balance.user_id === effectivePayerId)?.official_balance_cents ?? 0));
  const warning = amountCents > 0 && (amountCents > payeeBalance || (payerOwes > 0 && amountCents > payerOwes));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!effectivePayerId || !payeeId || effectivePayerId === payeeId || !Number.isInteger(amountCents) || amountCents <= 0) return;
    onCreatePayment({
      payer_id: effectivePayerId,
      payee_id: payeeId,
      amount_cents: amountCents,
    });
  };

  return (
    <Card className="p-4">
      <form className="flex flex-col gap-3" onSubmit={submit}>
        {canChoosePayer && (
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Payer
            <select
              aria-label="Payer"
              value={effectivePayerId}
              onChange={(event) => setPayerId(event.target.value)}
              className="h-10 rounded-lg border border-border-subtle bg-surface px-3 text-sm text-text-main"
            >
              <option value="">Select payer</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          Payee
          <select
            aria-label="Payee"
            value={payeeId}
            onChange={(event) => setPayeeId(event.target.value)}
            className="h-10 rounded-lg border border-border-subtle bg-surface px-3 text-sm text-text-main"
          >
            <option value="">Select payee</option>
            {users.filter((user) => user.id !== effectivePayerId).map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          Amount
          <input
            aria-label="Amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="h-10 rounded-lg border border-border-subtle bg-surface px-3 text-sm text-text-main"
          />
        </label>
        {warning && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-sm font-semibold text-yellow-800">
            This payment is higher than the current balance. You can still submit it.
          </div>
        )}
        {payeeId && payeeBalance > 0 && (
          <div className="text-xs text-text-muted">
            Suggested cap: {formatCurrency(payeeBalance / 100)}
          </div>
        )}
        <Button type="submit" size="sm" className="self-start text-white">Create Payment</Button>
      </form>
    </Card>
  );
}
